import {
  AddIcon,
  BagIcon,
  Box,
  DefaultCategoryIcon,
  Flex,
  Skeleton,
} from '@iheartjane/reefer';
import type { FlexProps } from '@iheartjane/reefer';
import type { ProductCardsConfig } from '@iheartjane/types';
import { clsx } from 'clsx';

import styles from './cardSkeleton.module.css';

const IconCta = ({
  cta_icon = 'add',
  ...props
}: FlexProps & { cta_icon?: ProductCardsConfig['cta_icon'] }) => (
  <Flex
    className={clsx(styles['icon-cta'])}
    alignItems="center"
    justifyContent="center"
    background="primary"
    borderRadius="lg"
    height={48}
    width={48}
    {...props}
  >
    {cta_icon === 'add' ? (
      <AddIcon color="grays-white" />
    ) : (
      <BagIcon color="grays-white" />
    )}
  </Flex>
);

export const ProductCardSkeleton = ({
  card_border = false,
  cta_icon = 'add',
  cta_style = 'card_bottom_full',
  hover = 'shadow',
}: ProductCardsConfig) => {
  return (
    <Flex
      className={clsx(styles['product-card'], {
        [styles['product-card__border']]: card_border,
        [styles['product-card__no_border']]: !card_border,
        [styles['product-card__hover_shadow']]: hover === 'shadow',
        [styles['product-card__hover_shadow_color']]: hover === 'shadow_color',
        [styles['product-card__hover_image_border']]: hover === 'image_border',
        [styles['product-card__hover_image_zoom']]: hover === 'image_zoom',
      })}
      borderRadius="lg"
      flexDirection="column"
      gap={12}
      p={card_border ? 16 : 0}
      width="100%"
    >
      {/* Card Image */}
      <Flex
        className={clsx(styles['product-card-image-container'])}
        alignItems="center"
        justifyContent="center"
        background="grays-ultralight"
        borderRadius="lg"
        position="relative"
        width="100%"
        style={{
          aspectRatio: 1,
        }}
      >
        {/* Specials Badge */}
        <Box
          className={styles['special-badge']}
          background="info"
          borderRadius="lg"
          position="absolute"
          height={24}
          width={72}
          top={cta_style === 'image_top' ? 'auto' : 12}
          bottom={cta_style === 'image_top' ? 12 : 'auto'}
          left={12}
        />

        {hover === 'image_zoom' && (
          <DefaultCategoryIcon
            className={styles['product-card-image']}
            color="grays-light"
            size="xxl"
          />
        )}

        {/* CTA, image_top/image_bottom variants */}
        {(cta_style === 'image_top' || cta_style === 'image_bottom') && (
          <IconCta
            cta_icon={cta_icon}
            position="absolute"
            bottom={cta_style === 'image_bottom' ? 12 : 'auto'}
            right={12}
            top={cta_style === 'image_top' ? 12 : 'auto'}
          />
        )}
      </Flex>

      {/* Card Text */}
      <Skeleton>
        <Flex flexDirection="column" gap={8}>
          <Skeleton.Bone height={18} width={72} />
          <Skeleton.Bone height={18} width={110} />
          <Box
            height={18}
            width="100%"
            maxWidth={160}
            borderRadius="lg"
            background="grays-light"
          />
          <Skeleton.Bone height={18} width={110} />
        </Flex>
      </Skeleton>

      <Flex alignItems="center" justifyContent="space-between">
        {/* Card Price */}
        <Box background="grays-mid" borderRadius="lg" height={20} width={72} />

        {/* CTA, card_bottom variant */}
        {cta_style === 'card_bottom' && <IconCta cta_icon={cta_icon} />}
      </Flex>

      {/* CTA, card_bottom_full variant */}
      {cta_style === 'card_bottom_full' && (
        <Box background="primary" borderRadius="lg" height={40} width="100%" />
      )}
    </Flex>
  );
};
